import { createSlice } from "@reduxjs/toolkit";

import { FEED_RECOVERY_TYPES } from "../components/ROPasses/ROPass/ROConfigDesign/ROFlowCalculator/ROFlowCalculator.constants";

import { RO_DEFAULT_FILTERS } from "./RODefaultFIlters";

// Per-node initial state — exported so selectors can use it as a safe fallback.
export const roNodeInitialState = {
  activePassIndex: 0,
  activeStageIndex: 0,
  isRODataUpdated: false,
  isCCRODataUpdated: false,
  shouldRecalculateCcroFlowCalculator: false,
  roDiagramPipeSize: "medium",
  roElementsActiveSort: "asc",
  roConfig: {},
  ccroConfig: {},
  roInvalidData: {},
  ccroInvalidData: {},
  roPassesElementsFilters: { pass0: RO_DEFAULT_FILTERS, pass1: RO_DEFAULT_FILTERS },
  roElements: { pass0: {}, pass1: {} },
  shouldRecalculateFlowCalculator: false,
};

// Top-level slice state — all per-node data lives inside `nodes`, keyed by nodeId.
const initialState = {
  nodes: {},
};

// Returns the mutable per-node sub-state, auto-initialising it when it doesn't
// exist yet. `nodeId` must always be supplied by callers.
const getNode = (state, nodeId) => {
  if (!nodeId) return null;
  if (!state.nodes[nodeId]) {
    state.nodes[nodeId] = { ...roNodeInitialState };
  }
  return state.nodes[nodeId];
};

export const ROConfigSlice = createSlice({
  name: "ROConfig",
  initialState,
  reducers: {
    // ── Node lifecycle ──────────────────────────────────────────────────────
    initializeRONode: (state, action) => {
      const { nodeId } = action.payload;
      if (nodeId && !state.nodes[nodeId]) {
        state.nodes[nodeId] = { ...roNodeInitialState };
      }
    },
    removeRONode: (state, action) => {
      const { nodeId } = action.payload;
      if (nodeId) delete state.nodes[nodeId];
    },

    // ── Data reducers ───────────────────────────────────────────────────────
    updatePassesFlowCalcData: (state, action) => {
      const { nodeId, flowCalculatorData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      flowCalculatorData.forEach((item, ind) => {
        node.roConfig.passes[ind].flowCalculator = item;
      });
    },
    updateRODiagramPipeSize: (state, action) => {
      const { nodeId, roDiagramPipeSize } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roDiagramPipeSize = roDiagramPipeSize;
    },
    updatePassERDData: (state, action) => {
      const { passIndex, data, isEnabling, nodeId } = action.payload;
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roConfig.passes[passIndex].flowCalculator = {
        ...node.roConfig.passes[passIndex].flowCalculator,
        concentrateRecycle: data,
      };
      node.roConfig.passes[passIndex].isEnergyRecoveryDevice = isEnabling;
      node.roConfig.passes[passIndex].flowCalculator.recoveryType = FEED_RECOVERY_TYPES.net;
      node.isRODataUpdated = true;
    },
    addROPass: (state, action) => {
      const { nodeId, pass } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;

      const previousPass = node.roConfig.passes[node.roConfig.passes.length - 1];
      const newPass = previousPass?.isSolveForRecovery ? { ...pass, isSolveForRecovery: true } : pass;

      node.roConfig.passes.push(newPass);
      node.isRODataUpdated = true;
      node.activePassIndex = node.activePassIndex + 1;
    },
    updateROConfig: (state, action) => {
      const { nodeId, ...configData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roConfig = { ...node.roConfig, ...configData };
    },
    updateCCROConfig: (state, action) => {
      const { nodeId, ...configData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.ccroConfig = { ...node.ccroConfig, ...configData };
    },
    updatePassesActiveIndex: (state, action) => {
      const { nodeId, passIndex } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.activePassIndex = passIndex;
    },
    setIsRODataUpdated: (state, action) => {
      const { nodeId, isRODataUpdated } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.isRODataUpdated = isRODataUpdated;
    },
    setIsCCRODataUpdated: (state, action) => {
      const { nodeId, isCCRODataUpdated } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.isCCRODataUpdated = isCCRODataUpdated;
    },
    updateROConfigData: (state, action) => {
      const { nodeId, ...configData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roConfig = { ...node.roConfig, ...configData };
      node.isRODataUpdated = true;
    },
    updateCCROConfigData: (state, action) => {
      const { nodeId, isCcroUpdated = true, ...configData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.ccroConfig = { ...node.ccroConfig, ...configData };

      if (isCcroUpdated) node.isCCRODataUpdated = true;
    },
    updateRODataStatus: (state, action) => {
      const { nodeId, ...rest } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roInvalidData = { ...node.roInvalidData, ...rest };
    },
    updateCCRODataStatus: (state, action) => {
      const { nodeId, ...rest } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.ccroInvalidData = { ...node.ccroInvalidData, ...rest };
    },
    mergeCCROPassInvalidState: (state, action) => {
      const { nodeId, passIndex, passInvalidState } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;

      const targetPassIndex = Number.isInteger(passIndex) ? passIndex : node.activePassIndex;
      const existingPassInvalidStateByIndex = node.ccroInvalidData?.passInvalidStateByIndex || {};

      node.ccroInvalidData = {
        ...node.ccroInvalidData,
        passInvalidStateByIndex: {
          ...existingPassInvalidStateByIndex,
          [targetPassIndex]: {
            ...(existingPassInvalidStateByIndex?.[targetPassIndex] || {}),
            ...(passInvalidState || {}),
          },
        },
      };
    },
    updateActivePassStageIndex: (state, action) => {
      const { passIdx, stageIdx, nodeId } = action.payload;
      const node = getNode(state, nodeId);
      if (!node) return;
      node.activeStageIndex = stageIdx;
      if (passIdx !== undefined) node.activePassIndex = passIdx;
    },
    updateROChemDegas: (state, action) => {
      const { passIndex, data, nodeId } = action.payload;
      const node = getNode(state, nodeId);
      if (!node) return;
      const index = passIndex ?? node.activePassIndex;
      node.roConfig.passes[index].chemicalDegas = {
        ...node.roConfig.passes[index].chemicalDegas,
        ...data,
      };
      node.isRODataUpdated = true;
    },
    updateROElements: (state, action) => {
      const { nodeId, ...elementsData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roElements = { ...node.roElements, ...elementsData };
    },
    updateROElementsFilter: (state, action) => {
      const { nodeId, ...filtersData } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roPassesElementsFilters = { ...node.roPassesElementsFilters, ...filtersData };
    },
    updateROElementsSort: (state, action) => {
      const { nodeId, roElementsActiveSort } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.roElementsActiveSort = roElementsActiveSort;
    },
    setShouldRecalculateFlowCalculator: (state, action) => {
      const { nodeId, shouldRecalculateFlowCalculator } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.shouldRecalculateFlowCalculator = shouldRecalculateFlowCalculator;
    },
    setShouldRecalculateCcroFlowCalculator: (state, action) => {
      const { nodeId, shouldRecalculateCcroFlowCalculator } = action.payload ?? {};
      const node = getNode(state, nodeId);
      if (!node) return;
      node.shouldRecalculateCcroFlowCalculator = shouldRecalculateCcroFlowCalculator;
    },
    // Applies Lithium mode to all RO/CCRO nodes by resetting filters, clearing selections, and triggering recalculation.
    updateRONodesForLithiumDesign: (state, action) => {
      state.nodes = action.payload;
    },
    // Resets the entire slice (all nodes) — used on project close.
    resetROConfigStoreData: () => initialState,
  },
});

export const {
  addROPass,
  initializeRONode,
  removeRONode,
  updateROConfig,
  updateCCROConfig,
  updateROElements,
  updateROChemDegas,
  updateRODataStatus,
  updateCCRODataStatus,
  mergeCCROPassInvalidState,
  updateROConfigData,
  updateCCROConfigData,
  setIsRODataUpdated,
  setIsCCRODataUpdated,
  updateROElementsSort,
  updateROElementsFilter,
  resetROConfigStoreData,
  updateRODiagramPipeSize,
  updatePassesActiveIndex,
  updatePassesFlowCalcData,
  updatePassERDData,
  updateActivePassStageIndex,
  setShouldRecalculateFlowCalculator,
  setShouldRecalculateCcroFlowCalculator,
  updateRONodesForLithiumDesign,
} = ROConfigSlice.actions;

export default ROConfigSlice.reducer;
